import { useEffect, useState } from 'react';
import Layout from '../../components/Layout';

const API = import.meta.env.VITE_API_URL || '/api';
const headers = () => ({
    Authorization: `Bearer ${localStorage.getItem('auth_token')}`,
    'Content-Type': 'application/json',
    Accept: 'application/json',
});

interface Campaign {
    id: string;
    name: string;
    slug: string;
    moderation_status: 'approved' | 'pending' | 'flagged' | 'banned';
    moderation_reason: string | null;
    tenant_id: string;
    updated_at: string;
}

interface Report {
    id: string;
    campaign_id: string;
    reason: 'gambling' | 'alcohol' | 'child_abuse' | 'inappropriate' | 'other';
    description: string | null;
    ip_address: string;
    status: 'pending' | 'resolved' | 'dismissed';
    created_at: string;
    campaign?: Campaign;
}

const reasonLabels: Record<string, string> = {
    gambling: '🔴 Cờ bạc trá hình / Cá độ',
    alcohol: '🍺 Quảng cáo rượu bia / Chất cấm',
    child_abuse: '👶 Nội dung độc hại / Trẻ em',
    inappropriate: '⚠️ Nội dung không lành mạnh khác',
    other: '🔍 Lý do khác',
};

export default function Moderation() {
    const [activeTab, setActiveTab] = useState<'reports' | 'campaigns'>('reports');
    
    // Reports State
    const [reports, setReports] = useState<Report[]>([]);
    const [reportsLoading, setReportsLoading] = useState(false);
    const [reportsTotal, setReportsTotal] = useState(0);
    const [reportsPage, setReportsPage] = useState(1);
    const [reportStatus, setReportStatus] = useState<'pending' | 'resolved' | 'dismissed'>('pending');

    // Campaigns State
    const [campaigns, setCampaigns] = useState<Campaign[]>([]);
    const [campaignsLoading, setCampaignsLoading] = useState(false);
    const [campaignsTotal, setCampaignsTotal] = useState(0);
    const [campaignsPage, setCampaignsPage] = useState(1);
    const [campaignFilter, setCampaignFilter] = useState<'pending' | 'flagged' | 'banned'>('flagged');

    // Action states
    const [banModalOpen, setBanModalOpen] = useState(false);
    const [selectedCampaign, setSelectedCampaign] = useState<Campaign | null>(null);
    const [banReason, setBanReason] = useState('');
    const [busy, setBusy] = useState(false);
    const [toast, setToast] = useState<{ msg: string; ok: boolean } | null>(null);

    const showToast = (msg: string, ok = true) => {
        setToast({ msg, ok });
        setTimeout(() => setToast(null), 3000);
    };

    const loadReports = async () => {
        setReportsLoading(true);
        try {
            const res = await fetch(`${API}/super/reports?status=${reportStatus}&page=${reportsPage}`, { headers: headers() });
            const data = await res.json();
            if (data.success) {
                setReports(data.data);
                setReportsTotal(data.meta.total);
            }
        } catch (err) {
            console.error(err);
            showToast('Không thể tải danh sách báo cáo.', false);
        } finally {
            setReportsLoading(false);
        }
    };

    const loadCampaigns = async () => {
        setCampaignsLoading(true);
        try {
            const res = await fetch(`${API}/super/moderated-campaigns?status=${campaignFilter}&page=${campaignsPage}`, { headers: headers() });
            const data = await res.json();
            if (data.success) {
                setCampaigns(data.data);
                setCampaignsTotal(data.meta.total);
            }
        } catch (err) {
            console.error(err);
            showToast('Không thể tải danh sách chiến dịch.', false);
        } finally {
            setCampaignsLoading(false);
        }
    };

    useEffect(() => {
        if (activeTab === 'reports') {
            loadReports();
        } else {
            loadCampaigns();
        }
    }, [activeTab, reportsPage, reportStatus, campaignsPage, campaignFilter]);

    // Xử lý báo cáo
    const handleResolveReport = async (reportId: string, status: 'resolved' | 'dismissed') => {
        if (!confirm(`Bạn có chắc muốn đánh dấu báo cáo này là ${status === 'resolved' ? 'đã xử lý' : 'bỏ qua'}?`)) return;
        setBusy(true);
        try {
            const res = await fetch(`${API}/super/reports/${reportId}/resolve`, {
                method: 'POST',
                headers: headers(),
                body: JSON.stringify({ status }),
            });
            const data = await res.json();
            if (data.success) {
                showToast('Đã cập nhật trạng thái báo cáo thành công.');
                loadReports();
            }
        } catch (err) {
            console.error(err);
            showToast('Thao tác thất bại.', false);
        } finally {
            setBusy(false);
        }
    };

    // Khóa chiến dịch (Ban)
    const openBanModal = (campaign: Campaign) => {
        setSelectedCampaign(campaign);
        setBanReason(campaign.moderation_reason || '');
        setBanModalOpen(true);
    };

    const handleBanCampaign = async () => {
        if (!selectedCampaign || !banReason.trim()) return;
        setBusy(true);
        try {
            const res = await fetch(`${API}/super/campaigns/${selectedCampaign.id}/ban`, {
                method: 'POST',
                headers: headers(),
                body: JSON.stringify({ reason: banReason }),
            });
            const data = await res.json();
            if (data.success) {
                showToast(`🔒 Đã khóa chiến dịch "${selectedCampaign.name}" thành công!`);
                setBanModalOpen(false);
                setSelectedCampaign(null);
                setBanReason('');
                if (activeTab === 'reports') loadReports();
                else loadCampaigns();
            }
        } catch (err) {
            console.error(err);
            showToast('Thao tác thất bại.', false);
        } finally {
            setBusy(false);
        }
    };

    // Mở khóa chiến dịch (Unban)
    const handleUnbanCampaign = async (campaign: Campaign) => {
        if (!confirm(`Bạn có chắc muốn mở khóa chiến dịch "${campaign.name}"?`)) return;
        setBusy(true);
        try {
            const res = await fetch(`${API}/super/campaigns/${campaign.id}/unban`, {
                method: 'POST',
                headers: headers(),
            });
            const data = await res.json();
            if (data.success) {
                showToast(`🔓 Đã mở khóa chiến dịch "${campaign.name}" thành công!`);
                if (activeTab === 'reports') loadReports();
                else loadCampaigns();
            }
        } catch (err) {
            console.error(err);
            showToast('Thao tác thất bại.', false);
        } finally {
            setBusy(false);
        }
    };

    return (
        <Layout>
            {/* Header */}
            <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4 mb-6">
                <div>
                    <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
                        🛡️ Kiểm Duyệt & Báo Cáo Vi Phạm
                    </h1>
                    <p className="text-sm text-gray-500 mt-1">
                        Quản lý nội dung cấm, cờ bạc trá hình, rượu bia và giải quyết báo cáo từ người chơi.
                    </p>
                </div>
            </div>

            {/* Tabs */}
            <div className="flex border-b border-gray-200 mb-6 bg-white p-1 rounded-xl shadow-sm max-w-md">
                <button
                    onClick={() => { setActiveTab('reports'); setReportsPage(1); }}
                    className={`flex-1 text-center py-2.5 rounded-lg text-sm font-bold transition-all ${
                        activeTab === 'reports' ? 'bg-blue-600 text-white shadow-sm' : 'text-gray-500 hover:text-gray-900'
                    }`}
                >
                    🚩 Báo Cáo Vi Phạm ({reportsTotal})
                </button>
                <button
                    onClick={() => { setActiveTab('campaigns'); setCampaignsPage(1); }}
                    className={`flex-1 text-center py-2.5 rounded-lg text-sm font-bold transition-all ${
                        activeTab === 'campaigns' ? 'bg-blue-600 text-white shadow-sm' : 'text-gray-500 hover:text-gray-900'
                    }`}
                >
                    📁 Chiến Dịch Bị Gắn Cờ ({campaignsTotal})
                </button>
            </div>

            {/* TAB 1: REPORTS */}
            {activeTab === 'reports' && (
                <div className="bg-white rounded-2xl border border-gray-200 shadow-sm overflow-hidden">
                    <div className="p-5 border-b border-gray-100 flex flex-wrap items-center justify-between gap-4 bg-gray-50/50">
                        <div className="flex items-center gap-2">
                            <span className="text-sm font-bold text-gray-500 uppercase">Trạng thái lọc:</span>
                            <div className="flex bg-gray-200/80 p-0.5 rounded-lg">
                                {['pending', 'resolved', 'dismissed'].map((status) => (
                                    <button
                                        key={status}
                                        onClick={() => { setReportStatus(status as any); setReportsPage(1); }}
                                        className={`px-3 py-1 rounded-md text-xs font-bold capitalize transition-all ${
                                            reportStatus === status ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-500 hover:text-gray-900'
                                        }`}
                                    >
                                        {status === 'pending' ? 'Chưa xử lý' : status === 'resolved' ? 'Đã khóa/Xử lý' : 'Đã bỏ qua'}
                                    </button>
                                ))}
                            </div>
                        </div>
                    </div>

                    {reportsLoading ? (
                        <div className="p-12 text-center text-gray-500 font-medium">Đang tải báo cáo...</div>
                    ) : reports.length === 0 ? (
                        <div className="p-12 text-center text-gray-400 font-medium">Không có báo cáo nào khớp bộ lọc.</div>
                    ) : (
                        <div className="overflow-x-auto">
                            <table className="w-full text-left border-collapse">
                                <thead>
                                    <tr className="bg-gray-50/70 border-b border-gray-100 text-xs font-bold text-gray-400 uppercase">
                                        <th className="p-4">Chiến dịch</th>
                                        <th className="p-4">Lý do báo cáo</th>
                                        <th className="p-4">Nội dung chi tiết</th>
                                        <th className="p-4">Địa chỉ IP</th>
                                        <th className="p-4">Ngày tạo</th>
                                        <th className="p-4 text-right">Thao tác</th>
                                    </tr>
                                </thead>
                                <tbody className="divide-y divide-gray-100 text-sm text-gray-700">
                                    {reports.map((report) => (
                                        <tr key={report.id} className="hover:bg-gray-50/50">
                                            <td className="p-4">
                                                {report.campaign ? (
                                                    <div>
                                                        <div className="font-bold text-gray-900">{report.campaign.name}</div>
                                                        <div className="text-xs font-semibold text-gray-400 font-mono">/{report.campaign.slug}</div>
                                                        <div className="mt-1">
                                                            <span className={`text-[10px] font-extrabold px-2 py-0.5 rounded-full ${
                                                                report.campaign.moderation_status === 'approved' ? 'bg-green-100 text-green-700' :
                                                                report.campaign.moderation_status === 'flagged' ? 'bg-amber-100 text-amber-700' :
                                                                'bg-red-100 text-red-700'
                                                            }`}>
                                                                {report.campaign.moderation_status.toUpperCase()}
                                                            </span>
                                                        </div>
                                                    </div>
                                                ) : (
                                                    <span className="text-gray-400 font-medium italic">Đã xóa chiến dịch</span>
                                                )}
                                            </td>
                                            <td className="p-4 font-semibold">
                                                {reasonLabels[report.reason] || report.reason}
                                            </td>
                                            <td className="p-4 max-w-xs break-words text-gray-500">
                                                {report.description || <span className="italic text-gray-300">Không ghi mô tả</span>}
                                            </td>
                                            <td className="p-4 font-mono text-xs text-gray-500">
                                                {report.ip_address}
                                            </td>
                                            <td className="p-4 text-xs text-gray-400 font-semibold">
                                                {new Date(report.created_at).toLocaleString('vi-VN')}
                                            </td>
                                            <td className="p-4 text-right">
                                                <div className="flex justify-end gap-2">
                                                    {report.status === 'pending' && report.campaign && (
                                                        <>
                                                            <button
                                                                onClick={() => openBanModal(report.campaign!)}
                                                                className="px-2.5 py-1.5 rounded-lg bg-red-50 text-red-600 hover:bg-red-100 text-xs font-bold transition-all"
                                                            >
                                                                🔒 Khóa chiến dịch
                                                            </button>
                                                            <button
                                                                onClick={() => handleResolveReport(report.id, 'dismissed')}
                                                                className="px-2.5 py-1.5 rounded-lg bg-gray-100 text-gray-600 hover:bg-gray-200 text-xs font-bold transition-all"
                                                            >
                                                                Bỏ qua
                                                            </button>
                                                        </>
                                                    )}
                                                    {report.status === 'pending' && !report.campaign && (
                                                        <button
                                                            onClick={() => handleResolveReport(report.id, 'dismissed')}
                                                            className="px-2.5 py-1.5 rounded-lg bg-gray-100 text-gray-600 hover:bg-gray-200 text-xs font-bold transition-all"
                                                        >
                                                            Đóng báo cáo
                                                        </button>
                                                    )}
                                                </div>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    )}
                </div>
            )}

            {/* TAB 2: CAMPAIGNS */}
            {activeTab === 'campaigns' && (
                <div className="bg-white rounded-2xl border border-gray-200 shadow-sm overflow-hidden">
                    <div className="p-5 border-b border-gray-100 flex flex-wrap items-center justify-between gap-4 bg-gray-50/50">
                        <div className="flex items-center gap-2">
                            <span className="text-sm font-bold text-gray-500 uppercase">Trạng thái lọc:</span>
                            <div className="flex bg-gray-200/80 p-0.5 rounded-lg">
                                {['pending', 'flagged', 'banned'].map((filter) => (
                                    <button
                                        key={filter}
                                        onClick={() => { setCampaignFilter(filter as any); setCampaignsPage(1); }}
                                        className={`px-3 py-1 rounded-md text-xs font-bold capitalize transition-all ${
                                            campaignFilter === filter ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-500 hover:text-gray-900'
                                        }`}
                                    >
                                        {filter === 'pending' ? 'Chờ duyệt AI' : filter === 'flagged' ? 'AI/User Cảnh báo' : '🔒 Đã khóa (Ban)'}
                                    </button>
                                ))}
                            </div>
                        </div>
                    </div>

                    {campaignsLoading ? (
                        <div className="p-12 text-center text-gray-500 font-medium">Đang tải chiến dịch...</div>
                    ) : campaigns.length === 0 ? (
                        <div className="p-12 text-center text-gray-400 font-medium">Không tìm thấy chiến dịch nào.</div>
                    ) : (
                        <div className="overflow-x-auto">
                            <table className="w-full text-left border-collapse">
                                <thead>
                                    <tr className="bg-gray-50/70 border-b border-gray-100 text-xs font-bold text-gray-400 uppercase">
                                        <th className="p-4">Chiến dịch</th>
                                        <th className="p-4">Lý do cấm / Cảnh báo</th>
                                        <th className="p-4">Cửa hàng (Tenant)</th>
                                        <th className="p-4">Ngày cập nhật</th>
                                        <th className="p-4 text-right">Thao tác</th>
                                    </tr>
                                </thead>
                                <tbody className="divide-y divide-gray-100 text-sm text-gray-700">
                                    {campaigns.map((campaign) => (
                                        <tr key={campaign.id} className="hover:bg-gray-50/50">
                                            <td className="p-4">
                                                <div>
                                                    <div className="font-bold text-gray-900">{campaign.name}</div>
                                                    <div className="text-xs font-semibold text-gray-400 font-mono">/{campaign.slug}</div>
                                                </div>
                                            </td>
                                            <td className="p-4 max-w-sm">
                                                <div className="text-gray-600 font-medium text-xs bg-red-50/50 border border-red-100 rounded-lg p-2.5">
                                                    {campaign.moderation_reason || 'Không ghi lý do cụ thể.'}
                                                </div>
                                            </td>
                                            <td className="p-4 font-mono text-xs text-gray-500">
                                                {campaign.tenant_id}
                                            </td>
                                            <td className="p-4 text-xs text-gray-400 font-semibold">
                                                {new Date(campaign.updated_at).toLocaleString('vi-VN')}
                                            </td>
                                            <td className="p-4 text-right">
                                                <div className="flex justify-end gap-2">
                                                    {campaign.moderation_status === 'banned' ? (
                                                        <button
                                                            onClick={() => handleUnbanCampaign(campaign)}
                                                            className="px-2.5 py-1.5 rounded-lg bg-green-50 text-green-700 hover:bg-green-100 text-xs font-bold transition-all"
                                                        >
                                                            🔓 Mở khóa
                                                        </button>
                                                    ) : (
                                                        <>
                                                            <button
                                                                onClick={() => openBanModal(campaign)}
                                                                className="px-2.5 py-1.5 rounded-lg bg-red-50 text-red-600 hover:bg-red-100 text-xs font-bold transition-all"
                                                            >
                                                                🔒 Khóa
                                                            </button>
                                                            <button
                                                                onClick={() => handleUnbanCampaign(campaign)} // unban ở đây hoạt động như nút Approve
                                                                className="px-2.5 py-1.5 rounded-lg bg-blue-50 text-blue-700 hover:bg-blue-100 text-xs font-bold transition-all"
                                                            >
                                                                Duyệt hợp lệ
                                                            </button>
                                                        </>
                                                    )}
                                                </div>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    )}
                </div>
            )}

            {/* BAN CAMPAIGN MODAL */}
            {banModalOpen && selectedCampaign && (
                <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4">
                    <div className="bg-white rounded-2xl max-w-md w-full p-6 shadow-2xl border border-gray-100 transform scale-100 transition-all">
                        <h3 className="text-lg font-extrabold text-gray-900 mb-2">
                            🔒 Khóa Chiến Dịch Vi Phạm
                        </h3>
                        <p className="text-xs text-gray-500 mb-4">
                            Chiến dịch <strong>{selectedCampaign.name}</strong> sẽ bị dừng hoạt động ngay lập tức. Hãy nhập lý do chi tiết để thông báo cho khách hàng của bạn.
                        </p>
                        <div className="mb-4">
                            <label className="block text-[11px] font-bold text-gray-400 uppercase mb-1">Lý do khóa</label>
                            <textarea
                                value={banReason}
                                onChange={(e) => setBanReason(e.target.value)}
                                placeholder="Ví dụ: Chiến dịch vi phạm chính sách cờ bạc trá hình (quay trúng quà tiền mặt giá trị cao quy đổi tự động)..."
                                className="w-full h-24 px-3 py-2 border border-gray-200 rounded-xl focus:ring-2 focus:ring-blue-100 focus:border-blue-500 outline-none text-sm font-semibold resize-none"
                            />
                        </div>
                        <div className="flex gap-3 justify-end">
                            <button
                                onClick={() => { setBanModalOpen(false); setSelectedCampaign(null); }}
                                className="px-4 py-2 text-xs font-bold text-gray-500 hover:text-gray-700 bg-gray-50 hover:bg-gray-100 rounded-xl transition-all"
                            >
                                Hủy bỏ
                            </button>
                            <button
                                onClick={handleBanCampaign}
                                disabled={busy || !banReason.trim()}
                                className="px-4 py-2 text-xs font-bold text-white bg-red-600 hover:bg-red-700 disabled:bg-gray-300 rounded-xl shadow-md shadow-red-100 transition-all"
                            >
                                {busy ? 'Đang xử lý...' : 'Xác nhận khóa'}
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Toast feedback */}
            {toast && (
                <div className={`fixed bottom-5 right-5 px-5 py-3 rounded-xl shadow-lg border text-sm font-bold z-50 transition-all ${
                    toast.ok ? 'bg-green-50 text-green-700 border-green-150 shadow-green-100' : 'bg-red-50 text-red-600 border-red-150 shadow-red-100'
                }`}>
                    {toast.ok ? '✅' : '❌'} {toast.msg}
                </div>
            )}
        </Layout>
    );
}
